/**
 * Espace Équipe (refonte v4 du 18/10/2026) : Disponibilités, Décisions et
 * Astreintes (19/10/2026), chacune à son adresse. Les anciennes adresses (`/personnel`, `/decisions`,
 * `/validations`), portées par des avis, l'accueil, le planning et des
 * signets, y mènent avec leurs paramètres relus.
 */
import { lazy, type ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import type { AppRoute } from '../../app/route-types.js';
import { TEAM_PATHS, availabilityTarget, decisionsTarget } from './team-model.js';
import { ONCALL_PATHS } from './oncall-model.js';

const TeamAvailabilityPage = lazy(() => import('./TeamAvailabilityPage.js').then((m) => ({ default: m.TeamAvailabilityPage })));
const TeamDecisionsPage = lazy(() => import('./TeamDecisionsPage.js').then((m) => ({ default: m.TeamDecisionsPage })));
const OncallPage = lazy(() => import('./OncallPage.js').then((m) => ({ default: m.OncallPage })));
const OncallPeriodPage = lazy(() => import('./OncallPeriodPage.js').then((m) => ({ default: m.OncallPeriodPage })));
const OncallRotationPage = lazy(() => import('./OncallRotationPage.js').then((m) => ({ default: m.OncallRotationPage })));
const EscalationPage = lazy(() => import('./EscalationPage.js').then((m) => ({ default: m.EscalationPage })));

function DecisionsRedirect(): ReactNode {
  const { search } = useLocation();
  return <Navigate to={decisionsTarget(search)} replace />;
}

function AvailabilityRedirect(): ReactNode {
  const { search } = useLocation();
  return <Navigate to={availabilityTarget(search)} replace />;
}

const DECIDE = { anyOf: ['absence.approve'] } as const;
const TEAM = { anyOf: ['planning.view_all', 'absence.approve'] } as const;
/** Planifier les astreintes : le droit d'organiser le planning de l'équipe (l'API revérifie la hiérarchie). */
const ONCALL_MANAGE = { anyOf: ['planning.edit_all'] } as const;

export const teamRoutes: AppRoute[] = [
  // Sans `planning.view_all`, la page des disponibilités mène aux décisions.
  { path: TEAM_PATHS.disponibilites, element: <TeamAvailabilityPage />, gate: TEAM },
  { path: TEAM_PATHS.decisions, element: <TeamDecisionsPage />, gate: DECIDE },
  // Astreintes (19/10/2026) : lues par tout membre, comme le planning ; planifiées par qui l'organise.
  { path: ONCALL_PATHS.liste, element: <OncallPage /> },
  { path: ONCALL_PATHS.nouvelle, element: <OncallPeriodPage />, gate: ONCALL_MANAGE },
  { path: ONCALL_PATHS.roulement, element: <OncallRotationPage />, gate: ONCALL_MANAGE },
  { path: ONCALL_PATHS.circuit, element: <EscalationPage />, gate: { anyOf: ['settings.manage'] } },
  { path: `${ONCALL_PATHS.liste}/:id`, element: <OncallPeriodPage />, gate: ONCALL_MANAGE },
  { path: '/personnel', element: <AvailabilityRedirect />, gate: TEAM },
  { path: '/decisions', element: <DecisionsRedirect />, gate: DECIDE },
  { path: '/validations', element: <DecisionsRedirect />, gate: DECIDE },
];
